/**
 * Torn API client.
 *
 * Two quirks drive the shape of this module:
 *  1. Torn returns HTTP 200 with `{"error":{"code":n,...}}` on failure, so every
 *     response body has to be unwrapped before it can be trusted.
 *  2. Item-market and bazaar data carry `cache_delay: 30` — it is globally cached
 *     for 30 seconds, so polling an item faster than that spends quota for
 *     nothing. The scheduler enforces the floor; this module just reports it.
 */

import { ApiError, getJson } from "./http.ts";
import type { ItemDetails, Offer } from "../state/types.ts";
import { toNumber } from "../util/format.ts";

const V1 = "https://api.torn.com";
const V2 = "https://api.torn.com/v2";

/** Shows up in the user's API log so a stray call is identifiable. */
const COMMENT = "DealSniper";

/** Torn's global cache window for market/bazaar selections, in seconds. */
export const TORN_CACHE_DELAY_SEC = 30;

interface TornErrorEnvelope {
  error?: { code: number; error: string };
}

function unwrap<T>(body: T & TornErrorEnvelope): T {
  if (body && typeof body === "object" && "error" in body && body.error) {
    throw new ApiError(body.error.error, body.error.code, "torn");
  }
  return body;
}

async function tornGet<T>(url: string, signal?: AbortSignal): Promise<T> {
  const body = await getJson<T & TornErrorEnvelope>(url, { signal, source: "torn" });
  return unwrap(body);
}

function withKey(base: string, key: string, params: Record<string, string | number> = {}): string {
  const search = new URLSearchParams({ key, comment: COMMENT });
  for (const [name, value] of Object.entries(params)) search.set(name, String(value));
  return `${base}${base.includes("?") ? "&" : "?"}${search.toString()}`;
}

/**
 * Error codes that mean "this key is finished" versus "back off and retry".
 * Full list: https://www.torn.com/swagger.php
 */
export const KEY_DEAD_CODES = new Set([1, 2, 6, 7, 10, 11, 12, 13, 16, 18]);
export const KEY_BACKOFF_CODES = new Set([5, 8, 9, 14, 15, 17, 24]);

export interface KeyIdentity {
  playerId: number;
  playerName: string;
}

/** Validates a key and identifies its owning account. */
export async function validateKey(key: string, signal?: AbortSignal): Promise<KeyIdentity> {
  const body = await tornGet<{ player_id?: number; name?: string }>(
    withKey(`${V1}/user/`, key, { selections: "basic" }),
    signal,
  );
  if (typeof body.player_id !== "number") {
    throw new ApiError("Key accepted but returned no player id", null, "torn");
  }
  return { playerId: body.player_id, playerName: body.name ?? `[${body.player_id}]` };
}

export interface CatalogRecord {
  id: number;
  name: string;
  type: string;
  subType: string | null;
  marketValue: number;
  tradable: boolean;
}

interface TornItemsBody {
  items?: Array<{
    id: number;
    name: string;
    type: string;
    sub_type?: string | null;
    is_tradable?: boolean;
    value?: { market_price?: number };
  }>;
}

/** The full item catalog — roughly 1 MB, so this is cached and rarely refetched. */
export async function fetchItemCatalog(key: string, signal?: AbortSignal): Promise<CatalogRecord[]> {
  const body = await tornGet<TornItemsBody>(withKey(`${V2}/torn/items`, key), signal);
  const items = body.items ?? [];
  return items.map((item) => ({
    id: item.id,
    name: item.name,
    type: item.type,
    subType: item.sub_type ?? null,
    marketValue: item.value?.market_price ?? 0,
    tradable: item.is_tradable !== false,
  }));
}

interface ItemMarketBody {
  itemmarket?: {
    item?: { id: number; name: string; type: string; average_price?: number };
    listings?: Array<{
      price: number;
      amount: number;
      item_details?: {
        uid?: number;
        rarity?: string | null;
        stats?: { damage?: number | null; accuracy?: number | null; quality?: number | null };
        bonuses?: Array<{ id?: number; title?: string; description?: string; value?: number }>;
      };
    }>;
    cache_timestamp?: number;
    cache_delay?: number;
  };
}

export interface ItemMarketResult {
  itemId: number;
  itemName: string;
  averagePrice: number;
  offers: Offer[];
  cacheTimestamp: number | null;
}

function normaliseRarity(raw: string | null | undefined): ItemDetails["rarity"] {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (lower.includes("red")) return "red";
  if (lower.includes("orange")) return "orange";
  if (lower.includes("yellow")) return "yellow";
  return null;
}

/**
 * Item-market listings for one item.
 *
 * `bonus` accepts a single WeaponBonusEnum value; Torn only supports one, so a
 * multi-bonus filter still has to be finished client-side.
 */
export async function fetchItemMarket(
  itemId: number,
  key: string,
  opts: { signal?: AbortSignal; limit?: number; bonus?: string | null } = {},
): Promise<ItemMarketResult> {
  const params: Record<string, string | number> = { limit: opts.limit ?? 100 };
  if (opts.bonus) params["bonus"] = opts.bonus;

  const body = await tornGet<ItemMarketBody>(
    withKey(`${V2}/market/${itemId}/itemmarket`, key, params),
    opts.signal,
  );

  const market = body.itemmarket;
  const fetchedAt = Date.now();
  const offers: Offer[] = (market?.listings ?? []).map((listing) => {
    const raw = listing.item_details;
    let details: ItemDetails | null = null;

    if (raw && typeof raw.uid === "number") {
      details = {
        uid: raw.uid,
        itemId,
        rarity: normaliseRarity(raw.rarity),
        damage: toNumber(raw.stats?.damage),
        accuracy: toNumber(raw.stats?.accuracy),
        quality: toNumber(raw.stats?.quality),
        bonuses: (raw.bonuses ?? [])
          .filter((bonus) => typeof bonus?.title === "string")
          .map((bonus) => ({
            name: bonus.title as string,
            value: typeof bonus.value === "number" ? bonus.value : 0,
            description: bonus.description,
          })),
      };
    }

    return {
      itemId,
      source: "market" as const,
      price: listing.price,
      quantity: listing.amount ?? 0,
      // The item market is anonymous — there is no seller to link to.
      sellerId: null,
      sellerName: null,
      details,
      lastChecked: null,
      fetchedAt,
    };
  });

  return {
    itemId,
    itemName: market?.item?.name ?? "",
    averagePrice: market?.item?.average_price ?? 0,
    offers,
    cacheTimestamp: market?.cache_timestamp ?? null,
  };
}

interface UserBazaarBody {
  bazaar?: Array<{ ID?: number; id?: number; name?: string; quantity?: number; price?: number }>;
  bazaar_is_open?: boolean;
}

/**
 * A single player's bazaar, straight from Torn.
 *
 * weav3r's aggregate can be up to ~60s stale, so a "deal" may already be gone.
 * This is the confirm-before-alert path: one extra Torn call to prove the
 * listing is still live at that price before making noise.
 */
export async function fetchUserBazaar(
  sellerId: number,
  itemId: number,
  key: string,
  signal?: AbortSignal,
): Promise<Offer[]> {
  const body = await tornGet<UserBazaarBody>(
    withKey(`${V1}/user/${sellerId}`, key, { selections: "bazaar" }),
    signal,
  );

  const fetchedAt = Date.now();
  const rows = body.bazaar ?? [];
  return rows
    .filter((row) => (row.ID ?? row.id) === itemId && typeof row.price === "number")
    .map((row) => ({
      itemId,
      source: "bazaar" as const,
      price: row.price as number,
      quantity: row.quantity ?? 0,
      sellerId,
      sellerName: null,
      details: null,
      lastChecked: null,
      fetchedAt,
    }));
}
